'use client';
import PageMainContent from '@c/products/PageMainContent';
import type { CategoryDocument } from '@models/category';
import useBikes from '@util/hooks/useBikes';
import type { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import type { Pagination } from '@util/types/paginator';
import { useAuth } from 'context/AuthContext';
import type { ProductDocument } from 'models/product';
import type { Make } from 'models/shared';
import { usePathname } from 'next/navigation';
import { useRef, useState } from 'react';
import { FindYourVehicle } from './components/FindYourVehicle';
import FloatingFilter from './components/FloatingFilter';
import VehicleFilterDrawer, {
  type DrawerPage,
} from './components/VehicleFilterDrawer';
import VehicleFilterSidebar from './components/VehicleFilterSidebar';
import VehicleForSaleControls from './components/VehicleForSaleControls';
import VehicleForSaleSeoSections from './components/VehicleForSaleSeoSections';
import { useVehicleForSaleFilters } from './hooks/useVehicleForSaleFilters';
import {
  ALL_VEHICLE_RIDE_TYPES,
  toRideType,
  type VehicleForSaleRideType,
} from './utils';

export interface VehicleForSaleProps {
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: TopFilters;
  initialSideFilters: PageSideFilters;
  categories: CategoryDocument[];
  manufacturerInfo?: any;
  popularCities: string[];
  allCities: string[];
  dirtBikeCats: CategoryDocument[];
  ridingGearCats: CategoryDocument[];
  /** The vehicle type this listing page serves. */
  rideType: VehicleForSaleRideType;
  /** The `*-for-sale` slug used to build listing URLs. */
  forSaleSlug: string;
}

const VehicleForSale = ({
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  manufacturerInfo,
  popularCities,
  allCities,
  dirtBikeCats,
  ridingGearCats,
  rideType,
  forSaleSlug,
}: VehicleForSaleProps) => {
  const pathname = usePathname();
  const { userDoc } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitialPage, setDrawerInitialPage] = useState<DrawerPage | null>(
    null
  );
  const controlsRef = useRef<HTMLDivElement>(null);

  const openDrawer = (page?: DrawerPage) => {
    setDrawerInitialPage(page ?? null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerInitialPage(null);
  };

  const {
    topFilters,
    sideFilters,
    handleSideFiltersChange,
    onBikeComplete,
    handleTopFiltersChange,
    clearAllFilters,
  } = useVehicleForSaleFilters({
    initialSideFilters,
    initialTopFilters,
    forSaleSlug,
  });

  const { bikeMakes } = useBikes(
    sideFilters.bikeYears,
    sideFilters.bikeMakes as Make[],
    rideType === 'All' ? ALL_VEHICLE_RIDE_TYPES : [toRideType(rideType)]
  );

  return (
    <div className="w-full sm:mx-auto">
      <div className="mt-0 flex w-full sm:-mb-16 sm:mt-8 sm:hidden sm:px-[9.5rem]">
        <FindYourVehicle
          onBikeComplete={onBikeComplete}
          sideFilters={sideFilters}
          hideYourSavedBikes={!!(userDoc && userDoc.bikes?.length)}
        />
      </div>
      <div className="mb-16 mt-24 hidden sm:my-0 sm:block">
        <FindYourVehicle
          onBikeComplete={onBikeComplete}
          sideFilters={sideFilters}
          hideYourSavedBikes={!!(userDoc && userDoc.bikes?.length)}
        />
      </div>

      <PageMainContent
        view={topFilters.view}
        clearAllFilters={clearAllFilters}
        isFetching={false}
        products={initialProducts}
        pageInfo={initialPageInfo}
        filterState={sideFilters}
        onFiltersChange={handleSideFiltersChange}
        attribution={{
          data_source: forSaleSlug.replace('-for-sale', ''),
          page: pathname ?? '',
          section: `category-page-${topFilters.view}`,
        }}
        filters={
          <VehicleFilterSidebar
            sideFilters={sideFilters}
            onChange={handleSideFiltersChange}
            bikeMakes={bikeMakes}
          />
        }
        searchControlsRef={controlsRef}
        controls={
          <VehicleForSaleControls
            topFilters={topFilters}
            onTopFiltersChange={handleTopFiltersChange}
            sideFilters={sideFilters}
            onSideFiltersChange={handleSideFiltersChange}
            onOpenDrawer={openDrawer}
          />
        }
      />

      <FloatingFilter
        onSort={() => openDrawer('sort')}
        onFilter={() => openDrawer()}
        anchorRef={controlsRef}
      />

      <VehicleFilterDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        sideFilters={sideFilters}
        onChange={handleSideFiltersChange}
        bikeMakes={bikeMakes}
        popularCities={popularCities}
        allCities={allCities}
        topFilters={topFilters}
        onTopFiltersChange={handleTopFiltersChange}
        initialPage={drawerInitialPage}
      />

      <VehicleForSaleSeoSections
        rideType={rideType}
        forSaleSlug={forSaleSlug}
        manufacturerInfo={manufacturerInfo}
        dirtBikeCats={dirtBikeCats}
        ridingGearCats={ridingGearCats}
      />
    </div>
  );
};

export default VehicleForSale;
